import { BREAKPOINTS } from "../../config/breakpoints";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Dropdown from "../../Generic/Dropdown";
import Tooltip from "../../Generic/Tooltip";
import Icons from "../../Generic/SvgIcons";
import { zIndex } from "../../config/zIndex";
import { AuditAction, AuditResource } from "../constants/auditLog.constants";
import { beautifyKey } from "../../util/util";
import { formatDisplayDate } from "../../util/date.util";
import { errorNotification } from "../../Generic/Notifications";
import InputBox from "../../Generic/InputBox";
import { get } from "../../config/api";

const StickyWrap = styled.div`
	position: sticky;
	top: 0;
	z-index: ${zIndex.stickyHeader};
	background: #f2f2f2;
	padding-bottom: 0.5rem;
`;

const FiltersCard = styled.div`
	width: 100%;
	margin: 1rem 0 0 0;
	background: #ffffff;
	border-radius: 12px;
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
	${'' /* overflow: hidden; */}
	border: 1px solid rgba(45, 35, 66, 0.1);
`;

const AccordionHeader = styled.div`
	background-color: ${(props) => (props.$expanded ? "" : "#f8f9fa")};
	padding: 12px 16px;
	cursor: pointer;
	transition: background-color 200ms linear;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const HeaderTop = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
`;

const CardTitle = styled.div`
	display: flex;
	align-items: center;
	font-size: 1.1rem;
	font-weight: 600;
	color: #333;
	margin: 0;

	svg {
		margin-right: 10px;
		color: rgba(45, 35, 66, 0.8);
	}
`;

const ActiveFilters = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0;
`;

const Badge = styled.div`
	display: inline-flex;
	align-items: center;
	background-color: #e6f2ff;
	color: rgba(45, 35, 66, 0.8);
	padding: 3px 8px;
	border-radius: 12px;
	font-size: 0.8rem;
	margin-right: 6px;
	margin-bottom: 4px;
	border: 1px solid #b3d7ff;
`;

const BadgeButton = styled.div`
	background: none;
	border: none;
	cursor: pointer;
	padding: 0;
	margin-left: 6px;
	color: rgba(45, 35, 66, 0.8);
	display: flex;
	align-items: center;
	&:hover {
		color: #0051cc;
	}
`;

const AccordionBody = styled.div`
	max-height: ${(props) => (props.$expanded ? "1200px" : "0")};
	opacity: ${(props) => (props.$expanded ? "1" : "0")};
	overflow: ${(props) => (props.$expanded ? "visible" : "hidden")};
	transition: 200ms max-height ease-in-out, 200ms opacity linear;
`;

const FiltersCardContent = styled.div`
	padding: 20px;
	border-top: 1px solid #e0e0e0;
`;

const ActiveFiltersText = styled.div`
	font-size: 0.9rem;
	margin: 0 0 8px 0;
	color: rgba(45, 35, 66, 0.8);
`;

const DraftTargets = styled.div`
	background-color: #f0f8ff;
	padding: 10px;
	border-radius: 8px;
	margin-bottom: 15px;
	border: 1px solid #d1e8ff;
`;

const FilterGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: 15px;

	@media (min-width: ${BREAKPOINTS.tablet}px) {
		grid-template-columns: 1fr 1fr;
	}
`;

const FilterItem = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	min-width: 0;

	& > div {
		width: 100%;
		box-sizing: border-box;
	}

	input {
		width: 100%;
		box-sizing: border-box;
	}
`;

const FilterLabel = styled.label`
	font-size: 0.8rem;
	font-weight: 600;
	color: #666;
	margin-bottom: 4px;
	display: flex;
	align-items: center;
	gap: 0.25rem;
`;

const AddButton = styled.button`
	background-color: rgba(45, 35, 66, 0.8);
	color: white;
	border: none;
	padding: 6px 12px;
	border-radius: 4px;
	font-size: 0.8rem;
	cursor: pointer;
	transition: background-color 0.2s;
	display: flex;
	align-items: center;
	white-space: nowrap;
	flex-shrink: 0;

	&:hover {
		background-color: rgba(45, 35, 66, 0.6);
	}

	svg {
		margin-right: 4px;
		width: 14px;
		height: 14px;
	}
`;

const ActionButton = styled.button`
	background-color: ${(props) => (props.$active ? "rgba(45, 35, 66, 0.8)" : "#fff")};
	color: ${(props) => (props.$active ? "#fff" : "rgba(45, 35, 66, 0.8)")};
	border: 1px solid ${(props) => (props.$active ? "transparent" : "#d1e8ff")};
	padding: 8px 16px;
	border-radius: 4px;
	font-size: 0.9rem;
	cursor: pointer;
	transition: background-color 0.2s;

	&:hover {
		background-color: ${(props) => (props.$active ? "rgba(45, 35, 66, 0.6)" : "#f0f8ff")};
	}
`;

const ButtonRow = styled.div`
	display: flex;
	gap: 0.75rem;
	margin-top: 15px;	
`;

const TargetRow = styled.div`
	display: grid;
	grid-template-columns: 1fr auto;
	gap: 8px;
	width: 100%;
	align-items: center;

	& > div {
		width: 100%;
		min-width: 0;
		box-sizing: border-box;
	}
`;

const StyledDropdown = styled(Dropdown)`
	width: 100%;
	box-shadow: none;
	border: 1px solid rgba(0, 0, 0, 0.6);
	outline: none;
	box-sizing: border-box;
`;

const actionOptions = Object.values(AuditAction).map((action) => ({
	head: beautifyKey(action),
	value: action,
}));

const emptyDraft = () => ({
	action: undefined,
	actorEmail: "",
	actorUid: "",
	targets: [],
	startDate: "",
	endDate: "",
});

/** Converts a datetime-local value to an ISO UTC string for the API. */
function toIsoUtc(datetimeLocal) {
	if (!datetimeLocal) {
		return undefined;
	}
	const date = new Date(datetimeLocal);
	if (Number.isNaN(date.getTime())) {
		return undefined;
	}
	return date.toISOString();
}

/** Expands machine chips into deduped RESTAURANT/KITCHEN targets for the API. */
function expandMachineTargets(machines = []) {
	const targets = [];
	const seen = new Set();

	machines.forEach((machine) => {
		const restaurantId = machine?.restaurantId;
		const kitchenId = machine?.kitchenId;

		if (restaurantId) {
			const restaurantKey = `${AuditResource.Restaurant}:${restaurantId}`;
			if (!seen.has(restaurantKey)) {
				seen.add(restaurantKey);
				targets.push({
					targetType: AuditResource.Restaurant,
					targetId: restaurantId,
				});
			}
		}

		if (kitchenId) {
			const kitchenKey = `${AuditResource.Kitchen}:${kitchenId}`;
			if (!seen.has(kitchenKey)) {
				seen.add(kitchenKey);
				targets.push({
					targetType: AuditResource.Kitchen,
					targetId: kitchenId,
				});
			}
		}
	});

	return targets;
}

function buildAppliedFromDraft(draft) {
	const applied = {};
	if (draft.action) {
		applied.action = draft.action;
	}
	const email = draft?.actorEmail?.trim();
	const uid = draft?.actorUid?.trim();
	if (email) {
		applied.actorEmail = email;
	}
	if (uid) {
		applied.actorUid = uid;
	}
	if (draft?.targets?.length > 0) {
		applied.targets = draft.targets;
	}
	const startDate = toIsoUtc(draft?.startDate);
	const endDate = toIsoUtc(draft?.endDate);
	if (startDate) {
		applied.startDate = startDate;
	}
	if (endDate) {
		applied.endDate = endDate;
	}
	return applied;
}

/** Converts UI filter state (machine chips) into the API request body shape. */
function toApiFilters(filters) {
	const apiFilters = { ...filters };
	if (filters?.targets?.length > 0) {
		apiFilters.targets = expandMachineTargets(filters.targets);
	} else {
		delete apiFilters.targets;
	}
	return apiFilters;
}

function buildActiveBadges(applied, handlers) {
	const badges = [];
	if (applied.action) {
		badges.push({
			key: "action",
			label: `Action: ${beautifyKey(applied.action)}`,
			onRemove: handlers.clearAction,
		});
	}
	if (applied.actorEmail) {
		badges.push({
			key: "email",
			label: `User email: ${applied.actorEmail}`,
			onRemove: handlers.clearEmail,
		});
	}
	if (applied.actorUid) {
		badges.push({
			key: "uid",
			label: `User id: ${applied.actorUid}`,
			onRemove: handlers.clearUid,
		});
	}
	if (applied.startDate) {
		badges.push({
			key: "startDate",
			label: `Start: ${formatDisplayDate(applied.startDate)}`,
			onRemove: handlers.clearStartDate,
		});
	}
	if (applied.endDate) {
		badges.push({
			key: "endDate",
			label: `End: ${formatDisplayDate(applied.endDate)}`,
			onRemove: handlers.clearEndDate,
		});
	}
	if(applied.targets?.length > 0) {
		applied.targets.forEach((machine, index) => {
			badges.push({
				key: `machine-${machine.restaurantId}`,
				label: `Machine: ${machine.restaurantId}`,
				onRemove: (event) => handlers.removeTarget(index, event),
			});
		});
	}
	return badges;
}

const AuditLogFilters = ({ onApply, onClear }) => {
	const [expanded, setExpanded] = useState(false);
	const [draft, setDraft] = useState(emptyDraft);
	const [applied, setApplied] = useState({});
	const [pendingMachineId, setPendingMachineId] = useState("");
	const [machines, setMachines] = useState([]);
	const [machinesLoaded, setMachinesLoaded] = useState(false);
	const [resetKey, setResetKey] = useState(0);

	const machineOptions = machines.map((machine) => ({
		head: machine._id,
		value: machine._id,
	}));

	async function loadMachines() {
		const response = await get({ url: "/v1/dashboard/restaurant/all" });
		const { data, error } = response;
		if (data) {
			setMachines(data);
			setMachinesLoaded(true);
		}
		if (error) {
			errorNotification(error);
			setMachines([]);
		}
	}

	useEffect(() => {
		if (machinesLoaded) {
			return;
		}
		loadMachines();
	}, [expanded, machinesLoaded]);

	/**
	 * Commit the applied filters to the state and call the onApply callback.
	 * @param {Object} nextApplied - The next applied filters.
	 * @param {Object} nextDraft - The next draft filters.
	 */
	function commitApplied(nextApplied, nextDraft) {
		setApplied(nextApplied);
		if (nextDraft) {
			setDraft(nextDraft);
		}
		onApply(toApiFilters(nextApplied));
	}

	function handleActionChange(value) {
		setDraft((prev) => ({
			...prev,
			action: value,
		}));
	}

	function addTargetChip() {
		if (!pendingMachineId) {
			return;
		}
		const machine = machines.find((item) => item._id === pendingMachineId);
		if (!machine) {
			return;
		}
		const isDuplicate = draft.targets.some(
			(target) => target.restaurantId === machine._id
		);
		if (isDuplicate) {
			return;
		}
		setDraft((prev) => ({
			...prev,
			targets: [
				...prev.targets,
				{
					restaurantId: machine._id,
					kitchenId: machine.kitchenId || "",
				},
			],
		}));
		setPendingMachineId("");
		setResetKey((key) => key + 1);
	}

	function removeDraftTargetChip(index) {
		setDraft((prev) => ({
			...prev,
			targets: prev.targets.filter((_, i) => i !== index),
		}));
	}

	function clearDraftAction() {
		setDraft((prev) => ({ ...prev, action: undefined }));
	}

	function clearDraftEmail() {
		setDraft((prev) => ({ ...prev, actorEmail: "" }));
	}

	function clearDraftUid() {
		setDraft((prev) => ({ ...prev, actorUid: "" }));
	}

	function clearDraftStartDate() {
		setDraft((prev) => ({ ...prev, startDate: "" }));
	}

	function clearDraftEndDate() {
		setDraft((prev) => ({ ...prev, endDate: "" }));
	}

	function clearActionFromApplied(event) {
		event?.stopPropagation();
		const nextDraft = { ...draft, action: undefined };
		const nextApplied = { ...applied };
		delete nextApplied.action;
		setResetKey((key) => key + 1);
		commitApplied(nextApplied, nextDraft);
	}

	function clearEmailFromApplied(event) {
		event?.stopPropagation();
		const nextDraft = { ...draft, actorEmail: "" };
		const nextApplied = { ...applied };
		delete nextApplied.actorEmail;
		commitApplied(nextApplied, nextDraft);
	}

	function clearUidFromApplied(event) {
		event?.stopPropagation();
		const nextDraft = { ...draft, actorUid: "" };
		const nextApplied = { ...applied };
		delete nextApplied.actorUid;
		commitApplied(nextApplied, nextDraft);
	}

	function clearStartDateFromApplied(event) {
		event?.stopPropagation();
		const nextDraft = { ...draft, startDate: "" };
		const nextApplied = { ...applied };
		delete nextApplied.startDate;
		commitApplied(nextApplied, nextDraft);
	}

	function clearEndDateFromApplied(event) {
		event?.stopPropagation();
		const nextDraft = { ...draft, endDate: "" };
		const nextApplied = { ...applied };
		delete nextApplied.endDate;
		commitApplied(nextApplied, nextDraft);
	}

	function removeTargetFromApplied(index, event) {
		event?.stopPropagation();
		const nextTargets = (applied.targets || []).filter((_, i) => i !== index);
		const nextDraft = { ...draft, targets: nextTargets };
		const nextApplied = { ...applied };
		if (nextTargets.length > 0) {
			nextApplied.targets = nextTargets;
		} else {
			delete nextApplied.targets;
		}
		commitApplied(nextApplied, nextDraft);
	}

	function handleApply() {
		if (draft.startDate && draft.endDate) {
			const start = new Date(draft.startDate);
			const end = new Date(draft.endDate);
			if (start > end) {
				errorNotification("Start time must be before or equal to end time.");
				return;
			}
		}
		const nextApplied = buildAppliedFromDraft(draft);
		setApplied((current) => ({ ...current, ...nextApplied }));
		onApply(toApiFilters(nextApplied));
		setExpanded(false);
		setDraft(emptyDraft());
		setPendingMachineId("");
	}

	function handleClear() {
		setDraft(emptyDraft());
		setApplied({});
		setPendingMachineId("");
		setResetKey((key) => key + 1);
		onClear();
		setExpanded(false);
	}

	const activeBadges = buildActiveBadges(applied, {
		clearAction: clearActionFromApplied,
		clearEmail: clearEmailFromApplied,
		clearUid: clearUidFromApplied,
		clearStartDate: clearStartDateFromApplied,
		clearEndDate: clearEndDateFromApplied,
		removeTarget: removeTargetFromApplied,
	});

	const draftBadges = buildActiveBadges(buildAppliedFromDraft(draft), {
		clearAction: clearDraftAction,
		clearEmail: clearDraftEmail,
		clearUid: clearDraftUid,
		clearStartDate: clearDraftStartDate,
		clearEndDate: clearDraftEndDate,
		removeTarget: (index) => removeDraftTargetChip(index),
	});

	return (
		<StickyWrap>
			<FiltersCard>
				<AccordionHeader $expanded={expanded} onClick={() => setExpanded((prev) => !prev)}>
					<HeaderTop>
						<CardTitle>
							{Icons.filter}
							<span>Filters</span>
						</CardTitle>
						<div className={expanded ? "rotate-up" : "rotate-down"}>
							<span className="material-symbols-outlined">expand_more</span>
						</div>
					</HeaderTop>
					{activeBadges.length > 0 ? (
						<ActiveFilters>
							{activeBadges.map((badge) => (
								<Badge key={badge.key} onClick={(event) => event.stopPropagation()}>
									{badge.label}
									<BadgeButton onClick={badge.onRemove}>{Icons.x}</BadgeButton>
								</Badge>
							))}
						</ActiveFilters>
					) : null}
				</AccordionHeader>

				<AccordionBody $expanded={expanded}>
					<FiltersCardContent>
						{draftBadges.length > 0 ? (
							<DraftTargets>
								<ActiveFiltersText>Pending filters</ActiveFiltersText>
								<div>
									{draftBadges.map((badge) => (
										<Badge key={badge.key}>
											{badge.label}
											<BadgeButton onClick={badge.onRemove}>{Icons.x}</BadgeButton>
										</Badge>
									))}
								</div>
							</DraftTargets>
						) : null}

						<FilterGrid>
							<FilterItem>
								<FilterLabel htmlFor="audit-action">Action</FilterLabel>
								<StyledDropdown
									key={`action-${resetKey}`}
									head="All actions"
									widthVal="100%"
									value={draft.action}
									optionsList={actionOptions}
									onChange={handleActionChange}
								/>
							</FilterItem>

							<FilterItem>
								<FilterLabel htmlFor="audit-email">User email</FilterLabel>
								<InputBox
									id="audit-email"
									type="text"
									width="100%"
									placeholder="Enter User email"
									value={draft.actorEmail}
									onChange={(e) => setDraft((prev) => ({ ...prev, actorEmail: e.target.value }))}
								/>
							</FilterItem>

							<FilterItem>
								<FilterLabel htmlFor="audit-uid">User id</FilterLabel>
								<InputBox
									id="audit-uid"
									type="text"
									width="100%"
									placeholder="Enter User id"
									value={draft.actorUid}
									onChange={(e) => setDraft((prev) => ({ ...prev, actorUid: e.target.value }))}
								/>
							</FilterItem>

							<FilterItem>
								<FilterLabel htmlFor="audit-start-date">Start time</FilterLabel>
								<InputBox
									id="audit-start-date"
									type="datetime-local"
									width="100%"
									value={draft.startDate}
									onChange={(e) => setDraft((prev) => ({ ...prev, startDate: e.target.value }))}
								/>
							</FilterItem>

							<FilterItem>
								<FilterLabel htmlFor="audit-end-date">End time</FilterLabel>
								<InputBox
									id="audit-end-date"
									type="datetime-local"
									width="100%"
									value={draft.endDate}
									onChange={(e) => setDraft((prev) => ({ ...prev, endDate: e.target.value }))}
								/>
							</FilterItem>

							<FilterItem>
								<FilterLabel>
									Targets
									<Tooltip text="Multiple targets include logs from any of those targets." />
								</FilterLabel>
								<TargetRow>
									<StyledDropdown
										key={`machine-${resetKey}`}
										head="Select Machine"
										widthVal="100%"
										value={pendingMachineId}
										optionsList={machineOptions}
										onChange={setPendingMachineId}
									/>
									<AddButton type="button" onClick={addTargetChip}>
										{Icons.plus}
										Add
									</AddButton>
								</TargetRow>
							</FilterItem>
						</FilterGrid>

						<ButtonRow>
							<ActionButton type="button" $active onClick={handleApply}>
								Apply
							</ActionButton>
							<ActionButton type="button" onClick={handleClear}>
								Clear
							</ActionButton>
						</ButtonRow>
					</FiltersCardContent>
				</AccordionBody>
			</FiltersCard>
		</StickyWrap>
	);
};

export default AuditLogFilters;
